'use client'

import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { FaFacebookMessenger, FaInstagram } from 'react-icons/fa'
import { MdLinkOff, MdRefresh, MdWarning } from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import { useAuth } from '@/store/hooks'
import { getDepartmentsByOrganization } from '@/lib/api/atendimento/departments'
import {
    connectMetaMessagingAccount,
    disconnectMetaMessagingAccount,
    getMetaMessagingAccounts,
    getMetaMessagingPages,
    MetaMessagingAccount,
    MetaMessagingChannel,
    MetaMessagingPage,
    updateMetaMessagingDepartment,
} from '@/lib/api/marketing/meta-api'

interface DepartmentOption {
    id: number
    name: string
}

const CHANNEL_LABEL: Record<MetaMessagingChannel, string> = {
    INSTAGRAM: 'Instagram Direct',
    MESSENGER: 'Messenger',
}

function ChannelIcon({ channel, className = '' }: { channel: MetaMessagingChannel; className?: string }) {
    return channel === 'INSTAGRAM' ? (
        <FaInstagram className={`text-pink-500 ${className}`} />
    ) : (
        <FaFacebookMessenger className={`text-blue-500 ${className}`} />
    )
}

/**
 * Instagram Direct e Messenger: as conversas entram no atendimento como as do
 * WhatsApp, respondidas pelo agente de IA do setor escolhido. Ver
 * docs/meta-inbound-and-instagram.md.
 */
export default function MetaMessagingSection() {
    const { user } = useAuth()
    const [accounts, setAccounts] = useState<MetaMessagingAccount[]>([])
    const [pages, setPages] = useState<MetaMessagingPage[] | null>(null)
    const [departments, setDepartments] = useState<DepartmentOption[]>([])
    const [newDepartmentId, setNewDepartmentId] = useState<number | ''>('')
    const [loading, setLoading] = useState(true)
    const [loadingPages, setLoadingPages] = useState(false)
    const [busyKey, setBusyKey] = useState<string | null>(null)
    const [toDisconnect, setToDisconnect] = useState<MetaMessagingAccount | null>(null)

    const loadAccounts = useCallback(async () => {
        const res = await getMetaMessagingAccounts()
        if (res.success) setAccounts(res.result ?? [])
    }, [])

    useEffect(() => {
        const run = async () => {
            setLoading(true)
            try {
                await loadAccounts()
                if (user?.organizationId) {
                    const res = await getDepartmentsByOrganization(user.organizationId)
                    if (res.success) {
                        const raw: any = res.result
                        const list: any[] = Array.isArray(raw) ? raw : raw?.data ?? []
                        setDepartments(list.map(d => ({ id: d.id, name: d.name })))
                    }
                }
            } finally {
                setLoading(false)
            }
        }
        run()
    }, [loadAccounts, user?.organizationId])

    const handleLoadPages = async () => {
        setLoadingPages(true)
        try {
            const res = await getMetaMessagingPages()
            if (res.success) {
                setPages(res.result ?? [])
            } else {
                toast.error(res.error || 'Não foi possível listar as Páginas da conta Meta')
            }
        } finally {
            setLoadingPages(false)
        }
    }

    const isConnected = (channel: MetaMessagingChannel, pageId: string) =>
        accounts.some(a => a.channel === channel && a.pageId === pageId)

    const handleConnect = async (channel: MetaMessagingChannel, page: MetaMessagingPage) => {
        if (!newDepartmentId) {
            toast.error('Escolha o setor que vai atender antes de conectar.')
            return
        }
        const key = `${channel}-${page.id}`
        setBusyKey(key)
        try {
            const res = await connectMetaMessagingAccount({
                channel,
                pageId: page.id,
                departmentId: Number(newDepartmentId),
            })
            if (res.success) {
                toast.success(`${CHANNEL_LABEL[channel]} conectado`)
                await loadAccounts()
            } else {
                toast.error(res.error || `Falha ao conectar o ${CHANNEL_LABEL[channel]}`)
            }
        } finally {
            setBusyKey(null)
        }
    }

    const handleDepartment = async (account: MetaMessagingAccount, value: string) => {
        setBusyKey(`dept-${account.id}`)
        try {
            const res = await updateMetaMessagingDepartment(account.id, value ? Number(value) : null)
            if (res.success) {
                toast.success('Setor atualizado')
                await loadAccounts()
            } else {
                toast.error(res.error || 'Falha ao trocar o setor')
            }
        } finally {
            setBusyKey(null)
        }
    }

    const handleDisconnect = async () => {
        if (!toDisconnect) return
        setBusyKey(`off-${toDisconnect.id}`)
        try {
            const res = await disconnectMetaMessagingAccount(toDisconnect.id)
            if (res.success) {
                toast.success('Conta desconectada')
                setToDisconnect(null)
                await loadAccounts()
            } else {
                toast.error(res.error || 'Falha ao desconectar')
            }
        } finally {
            setBusyKey(null)
        }
    }

    return (
        <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6 space-y-6">
            <div className="space-y-1">
                <h3 className="font-semibold text-base-content">Instagram Direct e Messenger</h3>
                <p className="text-sm text-base-content/60">
                    As mensagens entram no atendimento como as do WhatsApp e são respondidas pelo agente de IA do
                    setor escolhido. Contato novo abre oportunidade na triagem do funil.
                </p>
            </div>

            {/* Contas conectadas */}
            {loading ? (
                <div className="flex justify-center py-6">
                    <span className="loading loading-spinner loading-md text-secondary" />
                </div>
            ) : accounts.length === 0 ? (
                <p className="text-sm text-base-content/50">Nenhuma conta de mensagens conectada.</p>
            ) : (
                <div className="space-y-3">
                    {accounts.map(account => (
                        <div
                            key={account.id}
                            className="rounded-xl border border-base-300/60 p-4 flex flex-col md:flex-row md:items-center gap-4"
                        >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <ChannelIcon channel={account.channel} className="text-2xl shrink-0" />
                                <div className="min-w-0">
                                    <p className="font-medium text-base-content truncate">
                                        {account.channel === 'INSTAGRAM' && account.instagramUsername
                                            ? `@${account.instagramUsername}`
                                            : account.pageName || account.pageId}
                                    </p>
                                    <p className="text-xs text-base-content/60">
                                        {CHANNEL_LABEL[account.channel]}
                                        {account.channel === 'INSTAGRAM' && account.pageName
                                            ? ` · Página ${account.pageName}`
                                            : ''}
                                    </p>
                                    {!account.departmentId && (
                                        <p className="text-xs text-warning mt-1 flex items-center gap-1">
                                            <MdWarning /> Sem setor, as mensagens não entram no atendimento.
                                        </p>
                                    )}
                                    {account.lastError && (
                                        <p className="text-xs text-error/80 mt-1">{account.lastError}</p>
                                    )}
                                </div>
                            </div>

                            <div className="form-control w-full md:w-64">
                                <label className="label py-1">
                                    <span className="label-text text-xs font-medium">Setor que atende</span>
                                </label>
                                <select
                                    className="select select-bordered select-sm w-full"
                                    value={account.departmentId ?? ''}
                                    disabled={busyKey === `dept-${account.id}`}
                                    onChange={e => handleDepartment(account, e.target.value)}
                                >
                                    <option value="">Selecione…</option>
                                    {departments.map(d => (
                                        <option key={d.id} value={d.id}>
                                            {d.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button
                                className="btn btn-ghost btn-sm text-error gap-1 self-end md:self-center"
                                onClick={() => setToDisconnect(account)}
                            >
                                <MdLinkOff className="text-lg" /> Desconectar
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Conectar */}
            <div className="border-t border-base-300/60 pt-5 space-y-4">
                <div className="flex flex-col md:flex-row md:items-end gap-4">
                    <div className="form-control w-full md:w-72">
                        <label className="label py-1">
                            <span className="label-text text-sm font-medium">Setor para a nova conexão</span>
                        </label>
                        <select
                            className="select select-bordered w-full"
                            value={newDepartmentId}
                            onChange={e => setNewDepartmentId(e.target.value ? Number(e.target.value) : '')}
                        >
                            <option value="">Selecione…</option>
                            {departments.map(d => (
                                <option key={d.id} value={d.id}>
                                    {d.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button className="btn btn-outline btn-secondary gap-2" onClick={handleLoadPages} disabled={loadingPages}>
                        {loadingPages ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdRefresh className="text-lg" />
                        )}
                        {pages ? 'Atualizar Páginas' : 'Buscar Páginas'}
                    </button>
                </div>

                {pages && pages.length === 0 && (
                    <p className="text-sm text-base-content/60">
                        Nenhuma Página encontrada. Reconecte a conta Meta autorizando as permissões de mensagens.
                    </p>
                )}

                {pages && pages.length > 0 && (
                    <div className="space-y-2">
                        {pages.map(page => (
                            <div
                                key={page.id}
                                className="rounded-xl bg-base-200/40 px-4 py-3 flex flex-col md:flex-row md:items-center gap-3"
                            >
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm text-base-content truncate">{page.name || page.id}</p>
                                    <p className="text-xs text-base-content/60">
                                        {page.instagramUsername
                                            ? `Instagram ligado: @${page.instagramUsername}`
                                            : 'Sem conta do Instagram ligada'}
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        className="btn btn-sm gap-1"
                                        disabled={isConnected('MESSENGER', page.id) || busyKey === `MESSENGER-${page.id}`}
                                        onClick={() => handleConnect('MESSENGER', page)}
                                    >
                                        <ChannelIcon channel="MESSENGER" />
                                        {isConnected('MESSENGER', page.id) ? 'Messenger conectado' : 'Conectar Messenger'}
                                    </button>
                                    {page.instagramAccountId && (
                                        <button
                                            className="btn btn-sm gap-1"
                                            disabled={isConnected('INSTAGRAM', page.id) || busyKey === `INSTAGRAM-${page.id}`}
                                            onClick={() => handleConnect('INSTAGRAM', page)}
                                        >
                                            <ChannelIcon channel="INSTAGRAM" />
                                            {isConnected('INSTAGRAM', page.id) ? 'Instagram conectado' : 'Conectar Instagram'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <ConfirmModal
                isOpen={!!toDisconnect}
                onClose={() => setToDisconnect(null)}
                onConfirm={handleDisconnect}
                title="Desconectar conta de mensagens"
                message="As mensagens desta conta deixarão de entrar no atendimento. As conversas já recebidas continuam no histórico. Deseja continuar?"
                confirmButtonText="Desconectar"
                cancelButtonText="Cancelar"
                loading={!!toDisconnect && busyKey === `off-${toDisconnect.id}`}
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
